import React, { useEffect, useRef, useState } from 'react';

import { FormattedMessage, useIntl } from 'react-intl';

import { ErrorMessage, Field, HelperMessage, MessageWrapper, ValidMessage } from '@atlaskit/form';
import { Stack } from '@atlaskit/primitives/compiled';
import TextField from '@atlaskit/textfield';

import messages from '../../messages';
import { type FocusAskImpactedWorkData } from '../../types';

import {
	type ImpactedWorkResolutionState,
	type ImpactedWorkResolutionStatus,
	useImpactedWorkResolution,
} from './useImpactedWorkResolution';

const ResolutionFeedback = ({
	resolution,
}: {
	resolution: ImpactedWorkResolutionState;
}): JSX.Element | null => {
	switch (resolution.status) {
		case 'idle':
			return null;
		case 'resolving':
			return (
				<HelperMessage>
					<FormattedMessage {...messages.impactedWorkResolving} />
				</HelperMessage>
			);
		case 'invalidUrl':
			return (
				<ErrorMessage>
					<FormattedMessage {...messages.impactedWorkInvalidUrl} />
				</ErrorMessage>
			);
		case 'notFound':
			return (
				<ErrorMessage>
					<FormattedMessage {...messages.impactedWorkNotFound} />
				</ErrorMessage>
			);
		case 'resolved':
			return (
				<ValidMessage>
					<FormattedMessage {...messages.impactedWorkResolvedSuccess} />
				</ValidMessage>
			);
		default:
			return null;
	}
};

type ImpactedWorkFieldProps = {
	value: FocusAskImpactedWorkData | null;
	cloudId: string;
	onValueChange: (value: FocusAskImpactedWorkData | null) => void;
	onResolutionStatusChange?: (status: ImpactedWorkResolutionStatus) => void;
	isDisabled?: boolean;
	hideLabel?: boolean;
};

export const ImpactedWorkField = ({
	value,
	cloudId,
	onValueChange,
	onResolutionStatusChange,
	isDisabled = false,
	hideLabel = false,
}: ImpactedWorkFieldProps): JSX.Element => {
	const intl = useIntl();
	const labelText = intl.formatMessage(messages.impactedItemFieldLabel);
	const [inputValue, setInputValue] = useState(value?.link ?? '');
	const resolution = useImpactedWorkResolution(inputValue, cloudId, value);
	// Tracks whether we've already synced the resolved URL into inputValue (one-time).
	const hasSyncedResolvedUrl = useRef(false);

	const resolvedId = resolution.status === 'resolved' ? resolution.data.id : null;
	const resolvedLink = resolution.status === 'resolved' ? resolution.data.link : null;

	useEffect(() => {
		if (resolution.status === 'resolved') {
			// One-time: sync the resolved URL into the input so the editable field shows
			// the URL rather than an empty field when the user opens the edit modal.
			if (!hasSyncedResolvedUrl.current && resolvedLink) {
				setInputValue(resolvedLink);
				hasSyncedResolvedUrl.current = true;
			}
			if (value?.id !== resolution.data.id) {
				onValueChange(resolution.data);
			}
			return;
		}
		// Any non-resolved state means we don't have a valid impacted item to
		// commit — clear the value.
		if (value !== null) {
			onValueChange(null);
		}

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [resolution.status, resolvedId, resolvedLink, value, onValueChange]);

	// Notify the parent of every resolution-status change so it can gate form submission
	useEffect(() => {
		onResolutionStatusChange?.(resolution.status);
	}, [resolution.status, onResolutionStatusChange]);

	const isInvalidInput = resolution.status === 'invalidUrl' || resolution.status === 'notFound';

	if (hideLabel) {
		return (
			<Stack space="space.100">
				<TextField
					name="impacted-work"
					value={inputValue}
					onChange={(event) => setInputValue(event.currentTarget.value)}
					placeholder={intl.formatMessage(messages.impactedWorkPlaceholder)}
					isInvalid={isInvalidInput}
					isDisabled={isDisabled}
					aria-label={labelText}
				/>
				<MessageWrapper>
					<ResolutionFeedback resolution={resolution} />
				</MessageWrapper>
			</Stack>
		);
	}

	return (
		<Stack space="space.100">
			<Field name="impacted-work" label={labelText}>
				{({ fieldProps }) => (
					<>
						<TextField
							{...fieldProps}
							value={inputValue}
							onChange={(event) => setInputValue(event.currentTarget.value)}
							placeholder={intl.formatMessage(messages.impactedWorkPlaceholder)}
							isInvalid={isInvalidInput}
							isDisabled={isDisabled}
						/>
						<MessageWrapper>
							<ResolutionFeedback resolution={resolution} />
						</MessageWrapper>
					</>
				)}
			</Field>
		</Stack>
	);
};
